/**
 * VideoCallWindow - Opens video call in a separate popup window
 * Allows users to continue using chat while on a call
 *
 * The React tree below is rendered into the popup's DOM but runs in THIS window's JS realm
 * (createRoot is called from here), which has two consequences the rest of this module has to
 * carry: closing the popup does not stop the Chime session - the root must be unmounted so
 * VideoCallContent's cleanup runs - and `window` inside the call UI is this window, never the
 * popup, so the popup is only ever closed from here.
 */

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom/client';
import {
    MeetingProvider
} from 'amazon-chime-sdk-component-library-react';
import { ThemeProvider } from 'styled-components';
import VideoCallContent from './VideoCallContent';
import { useChimeTheme } from './useCallTheme';

// Singleton to track popup window
let popupWindow = null;
let popupRoot = null;
let popupCallId = null;
let popupBeforeUnload = null;

/**
 * Mirrors the app's theme classes onto the popup document.
 * The popup's <html> starts bare, so every `.dark` / `.enhanced-*` rule in the stylesheets we
 * copy across would otherwise miss and the call would render in light-theme colours.
 */
function applyThemeToPopup(popupWin) {
    if (!popupWin || popupWin.closed) return;

    const appRoot = document.documentElement;
    const popupRootEl = popupWin.document.documentElement;
    popupRootEl.className = appRoot.className;

    const dataTheme = appRoot.getAttribute('data-theme');
    if (dataTheme) {
        popupRootEl.setAttribute('data-theme', dataTheme);
    } else {
        popupRootEl.removeAttribute('data-theme');
    }
}

/**
 * Shows a plain-DOM status line in the popup.
 *
 * The window is now opened before the call exists (VideoCallProvider opens it on the click
 * itself, so the browser cannot block it), which leaves it empty for as long as the
 * camera/microphone prompt and the meeting request take. An empty window reads as a broken
 * one, so say what is happening until React takes the window over.
 */
function showPopupStatus(popupWin, text) {
    if (!popupWin || popupWin.closed || !text) return;

    const existing = popupWin.document.getElementById('video-call-status');
    if (existing) {
        existing.textContent = text;
        return;
    }

    const status = popupWin.document.createElement('div');
    status.id = 'video-call-status';
    status.textContent = text;
    popupWin.document.body.appendChild(status);
}

function clearPopupStatus(popupWin) {
    if (!popupWin || popupWin.closed) return;
    const status = popupWin.document.getElementById('video-call-status');
    if (status) {
        status.remove();
    }
}

/**
 * Binds the popup's close handler, replacing whatever was bound before.
 *
 * The window can be opened before its call is known (and adopted by the join that follows a
 * 409 from /start), so the handler that knows which call to leave is not always the one bound
 * when the window was opened.
 */
function bindPopupClose(popupWin, onClose) {
    if (popupWindow && popupBeforeUnload) {
        popupWindow.removeEventListener('beforeunload', popupBeforeUnload);
    }

    popupBeforeUnload = () => {
        // Unmount first, while the popup document still exists: that cleanup is what leaves
        // the Chime meeting and stops the microphone.
        unmountPopupRoot();
        popupWindow = null;
        popupCallId = null;
        popupBeforeUnload = null;
        if (onClose) {
            onClose();
        }
    };
    popupWin.addEventListener('beforeunload', popupBeforeUnload);
}

/**
 * Unmounts the call UI, which is what releases the camera and microphone.
 * Closing the popup window on its own does not: the Chime session lives in this realm.
 */
function unmountPopupRoot() {
    if (!popupRoot) return;

    const root = popupRoot;
    popupRoot = null;
    try {
        root.unmount();
    } catch (error) {
        console.error('[VideoCall] Error unmounting call window content:', error);
    }
}

/**
 * Opens a popup window for video call
 * Returns the window reference
 */
export function openVideoCallWindow(callData, onClose) {
    // If a popup for THIS call is already open, focus it. A window that has nothing rendered
    // into it yet is kept too: that is the window opened for a call that is still being set
    // up, which the same flow comes back to once it knows what it is joining (/start
    // answering 409, so the call is joined instead of created). Reopening would mean a second
    // window.open from a continuation with no user activation left - exactly what the browser
    // blocks, and the reason the window is opened up front at all.
    //
    // A popup left over from an earlier call must never be recycled: React would keep the
    // previous VideoCallContent instance (isJoined already true), so the new meeting would
    // never be joined and the window would sit there showing the finished call.
    if (popupWindow && !popupWindow.closed) {
        if (popupCallId === callData.callId || !popupRoot) {
            popupCallId = callData.callId ?? null;
            showPopupStatus(popupWindow, callData.statusText);
            bindPopupClose(popupWindow, onClose);
            popupWindow.focus();
            return popupWindow;
        }
        closeVideoCallWindow();
    }

    // Calculate centered position
    const width = 900;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    // Open popup window
    popupWindow = window.open(
        '',
        'VideoCall',
        `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,menubar=no,toolbar=no,location=no`
    );

    if (!popupWindow) {
        // Blocked. The caller has to abandon the call - there is no other call UI - so say
        // nothing here and let it report the failure through callState.error.
        popupWindow = null;
        return null;
    }

    popupCallId = callData.callId ?? null;

    // Copy stylesheets from parent
    const parentStyles = document.querySelectorAll('link[rel="stylesheet"], style');
    parentStyles.forEach(style => {
        const clone = style.cloneNode(true);
        popupWindow.document.head.appendChild(clone);
    });

    // Carry the theme across, then add base styles for the popup
    applyThemeToPopup(popupWindow);

    const popupStyles = popupWindow.document.createElement('style');
    popupStyles.textContent = `
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: hsl(var(--background, 240 20% 4%));
            overflow: hidden;
        }
        #video-call-root {
            width: 100vw;
            height: 100vh;
        }
        #video-call-status {
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            color: hsl(var(--foreground, 0 0% 100%));
            font-size: 15px;
            /* Nothing to click while it is up, and it covers the whole window: never let it
               swallow a click meant for the call underneath if it ever outlives the render. */
            pointer-events: none;
        }
    `;
    popupWindow.document.head.appendChild(popupStyles);

    // Create root element
    const rootElement = popupWindow.document.createElement('div');
    rootElement.id = 'video-call-root';
    popupWindow.document.body.appendChild(rootElement);

    // Say what the window is waiting for until the call is rendered into it
    showPopupStatus(popupWindow, callData.statusText);

    // Handle popup close (the user closing the window from the OS chrome)
    bindPopupClose(popupWindow, onClose);

    return popupWindow;
}

/**
 * Wraps the call UI in the popup with the theme the app is currently using and keeps the
 * popup document's theme classes in step with it.
 */
function PopupCallContent({ popupWin, ...props }) {
    const { t } = useTranslation();
    const chimeTheme = useChimeTheme();

    useEffect(() => {
        if (!popupWin) return;

        applyThemeToPopup(popupWin);

        // Watch <html> directly rather than keying this on the Chime theme: that only flips
        // between light and dark, so switching light <-> enhanced-light mid-call left the
        // popup carrying the previous theme's classes.
        const observer = new MutationObserver(() => applyThemeToPopup(popupWin));
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class', 'data-theme']
        });
        return () => observer.disconnect();
    }, [popupWin]);

    useEffect(() => {
        if (!popupWin || popupWin.closed) return;
        popupWin.document.title = props.callType === 'video'
            ? t('aiChat.calls.videoCall')
            : t('aiChat.calls.audioCall');
    }, [popupWin, props.callType, t]);

    return (
        <ThemeProvider theme={chimeTheme}>
            <MeetingProvider>
                <VideoCallContent {...props} isPopup={true} />
            </MeetingProvider>
        </ThemeProvider>
    );
}

/**
 * Renders video call content into the popup window
 */
export function renderVideoCallInPopup(popupWin, props) {
    if (!popupWin || popupWin.closed) return;

    const rootElement = popupWin.document.getElementById('video-call-root');
    if (!rootElement) return;

    // The call is about to be on screen, so the 'Calling...' placeholder has done its job
    clearPopupStatus(popupWin);

    // The window may have been opened before this call had an id (see openVideoCallWindow):
    // record it now, so a later call for a different id is given a window of its own instead
    // of being handed this one.
    if (props.callId) {
        popupCallId = props.callId;
    }

    // Create React root if not exists
    if (!popupRoot) {
        popupRoot = ReactDOM.createRoot(rootElement);
    }

    // Render the video call content
    popupRoot.render(
        <PopupCallContent popupWin={popupWin} {...props} />
    );
}

/**
 * Closes the popup window
 */
export function closeVideoCallWindow() {
    if (popupWindow && popupBeforeUnload) {
        // Closing the window ourselves is not the user hanging up: drop the listener so the
        // owner's onClose (which posts /leave and resets state) does not fire for it.
        popupWindow.removeEventListener('beforeunload', popupBeforeUnload);
    }
    popupBeforeUnload = null;

    // Unmount before closing so the Chime session is torn down while its DOM still exists
    unmountPopupRoot();

    if (popupWindow && !popupWindow.closed) {
        popupWindow.close();
    }
    popupWindow = null;
    popupCallId = null;
}

/**
 * Check if popup window is open
 */
export function isVideoCallWindowOpen() {
    return popupWindow && !popupWindow.closed;
}

export default {
    openVideoCallWindow,
    renderVideoCallInPopup,
    closeVideoCallWindow,
    isVideoCallWindowOpen
};
